import React, { useMemo, useState, createElement, Component } from 'react';
import { Search, Download, Clock, TrendingDown, TrendingUp, Calendar, AlertTriangle, Truck, CheckCircle2, XCircle } from 'lucide-react';
import { KpiCard } from '../KpiCard';
const CARRIER_LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png'
};
type DelayStatus = 'In corso' | 'Recuperato' | 'Consegnato in ritardo';
type Delay = {
  id: string;
  shipmentRef: string;
  carrier: string;
  recipient: string;
  city: string;
  scheduledDate: string;
  daysLate: number;
  status: DelayStatus;
  reason: string;
};
const DELAYS: Delay[] = [{
  id: 'RIT-2401',
  shipmentRef: 'SH-7H2K9P',
  carrier: 'BRT',
  recipient: 'Boutique Centrale',
  city: 'Roma',
  scheduledDate: '23 mag 2026',
  daysLate: 3,
  status: 'In corso',
  reason: 'Mancato passaggio del corriere'
}, {
  id: 'RIT-2398',
  shipmentRef: 'SH-3M8N1Q',
  carrier: 'GLS',
  recipient: 'Store Milano Duomo',
  city: 'Milano',
  scheduledDate: '22 mag 2026',
  daysLate: 2,
  status: 'In corso',
  reason: 'Indirizzo errato — in correzione'
}, {
  id: 'RIT-2392',
  shipmentRef: 'SH-9F4D2R',
  carrier: 'FedEx',
  recipient: 'Mario Bianchi',
  city: 'Firenze',
  scheduledDate: '21 mag 2026',
  daysLate: 1,
  status: 'In corso',
  reason: 'Pacco trattenuto in hub'
}, {
  id: 'RIT-2380',
  shipmentRef: 'SH-5J7L8X',
  carrier: 'UPS',
  recipient: 'Concept Store Napoli',
  city: 'Napoli',
  scheduledDate: '19 mag 2026',
  daysLate: 4,
  status: 'Consegnato in ritardo',
  reason: 'Sciopero corriere locale'
}, {
  id: 'RIT-2371',
  shipmentRef: 'SH-2K6V4T',
  carrier: 'BRT',
  recipient: 'Outlet Bologna',
  city: 'Bologna',
  scheduledDate: '18 mag 2026',
  daysLate: 2,
  status: 'Consegnato in ritardo',
  reason: 'Maltempo — sospensione consegne'
}, {
  id: 'RIT-2354',
  shipmentRef: 'SH-8P1S3W',
  carrier: 'GLS',
  recipient: 'Anna Verdi',
  city: 'Torino',
  scheduledDate: '15 mag 2026',
  daysLate: 1,
  status: 'Recuperato',
  reason: 'Riconsegnato senza ritardo aggiuntivo'
}, {
  id: 'RIT-2341',
  shipmentRef: 'SH-6B9C5Y',
  carrier: 'SDA',
  recipient: 'Atelier Venezia',
  city: 'Venezia',
  scheduledDate: '13 mag 2026',
  daysLate: 5,
  status: 'Consegnato in ritardo',
  reason: 'Smarrimento temporaneo — recuperato dopo 3 giorni'
}, {
  id: 'RIT-2329',
  shipmentRef: 'SH-4N2Z7H',
  carrier: 'FedEx',
  recipient: 'Store Genova',
  city: 'Genova',
  scheduledDate: '12 mag 2026',
  daysLate: 2,
  status: 'Consegnato in ritardo',
  reason: 'Errore di smistamento'
}];
const STATUS_CONFIG: Record<DelayStatus, {
  bg: string;
  text: string;
  border: string;
  icon: ComponentType<any>;
}> = {
  'In corso': {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    icon: Clock
  },
  Recuperato: {
    bg: 'bg-[#D4FF3A]/10',
    text: 'text-[#D4FF3A]',
    border: 'border-[#D4FF3A]/30',
    icon: CheckCircle2
  },
  'Consegnato in ritardo': {
    bg: 'bg-[#5DA9FF]/10',
    text: 'text-[#5DA9FF]',
    border: 'border-[#5DA9FF]/30',
    icon: TrendingDown
  }
};
export function Ritardi({
  onOpenShipment
}: {
  onOpenShipment?: (shipmentId: string) => void;
} = {}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Tutti' | DelayStatus>('Tutti');
  const filtered = useMemo(() => DELAYS.filter(d => {
    const matchQ = !query.trim() || `${d.id} ${d.shipmentRef} ${d.recipient} ${d.city} ${d.carrier}`.toLowerCase().includes(query.toLowerCase());
    const matchS = statusFilter === 'Tutti' || d.status === statusFilter;
    return matchQ && matchS;
  }), [query, statusFilter]);
  const inProgress = DELAYS.filter(d => d.status === 'In corso');
  const historical = DELAYS.filter(d => d.status !== 'In corso');
  const avgDays = (DELAYS.reduce((acc, d) => acc + d.daysLate, 0) / DELAYS.length).toFixed(1);
  const recoveredRate = (DELAYS.filter(d => d.status === 'Recuperato').length / DELAYS.length * 100).toFixed(0);
  const handleExportCsv = () => {
    const header = ['ID Ritardo', 'Spedizione', 'Corriere', 'Destinatario', 'Città', 'Data prevista', 'Giorni ritardo', 'Stato', 'Motivo'];
    const rows = historical.map(d => [d.id, d.shipmentRef, d.carrier, d.recipient, d.city, d.scheduledDate, String(d.daysLate), d.status, d.reason]);
    const csv = [header, ...rows].map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `storico_ritardi_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <div className="space-y-6" data-id="element-2510">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-2511">
        <div data-id="element-2512">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-2513">
            Ritardi
          </h1>
        </div>
        <button type="button" onClick={handleExportCsv} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-2514">
          <Download className="h-4 w-4" data-id="element-2515" />
          Scarica storico CSV
        </button>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2516">
        <KpiCard label="Ritardi in corso" value={String(inProgress.length)} data-id="element-2517" />
        <KpiCard label="Storico ritardi" value={String(historical.length)} data-id="element-2518" />
        <KpiCard label="Giorni medi di ritardo" value={`${avgDays} g`} data-id="element-2519" />
        <KpiCard label="% recuperati" value={`${recoveredRate}%`} data-id="element-2520" />
      </section>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-2521">
        <div className="p-4 sm:p-5 flex items-center gap-3 flex-wrap border-b border-[#1F1F1F]" data-id="element-2522">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] overflow-x-auto" data-id="element-2523">
            {(['Tutti', 'In corso', 'Recuperato', 'Consegnato in ritardo'] as const).map(s => <button key={s} onClick={() => setStatusFilter(s)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${statusFilter === s ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-2524">
                {s}
              </button>)}
          </div>
          <div className="relative ml-auto w-full sm:w-auto" data-id="element-2525">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2526" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca per ID, destinatario, città..." className="w-full sm:w-72 pl-9 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-2527" />
          </div>
        </div>

        {filtered.length === 0 ? <div className="p-12 text-center" data-id="element-2528">
            <Clock className="h-8 w-8 text-[#6B6B6B] mx-auto mb-3" data-id="element-2529" />
            <p className="text-sm text-[#9A9A9A]" data-id="element-2530">Nessun ritardo trovato.</p>
          </div> : <div className="overflow-x-auto" data-id="element-2531">
            <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-2532">
              <thead data-id="element-2533">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-2534">
                  <th className="px-5 py-3 font-medium" data-id="element-2535">ID</th>
                  <th className="px-5 py-3 font-medium" data-id="element-2536">Spedizione</th>
                  <th className="px-5 py-3 font-medium" data-id="element-2537">Corriere</th>
                  <th className="px-5 py-3 font-medium" data-id="element-2538">Destinatario</th>
                  <th className="px-5 py-3 font-medium" data-id="element-2539">Data prevista</th>
                  <th className="px-5 py-3 font-medium text-right" data-id="element-2540">
                    Giorni ritardo
                  </th>
                  <th className="px-5 py-3 font-medium" data-id="element-2541">Motivo</th>
                  <th className="px-5 py-3 font-medium" data-id="element-2542">Stato</th>
                </tr>
              </thead>
              <tbody data-id="element-2543">
                {filtered.map(d => {
              const cfg = STATUS_CONFIG[d.status];
              const Icon = cfg.icon;
              return <tr key={d.id} onClick={() => onOpenShipment?.(d.shipmentRef)} className={`border-b border-[#1F1F1F] last:border-0 transition-colors ${onOpenShipment ? 'hover:bg-[#0A0A0A] cursor-pointer' : ''}`} data-id="element-2544">
                      <td className="px-5 py-3.5 font-mono text-xs text-[#9A9A9A]" data-id="element-2545">
                        {d.id}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-[#FAFAFA]" data-id="element-2546">
                        {d.shipmentRef}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-2547">
                        <div className="flex items-center gap-2.5" data-id="element-2548">
                          <div className="h-7 w-9 bg-white rounded p-1 flex items-center justify-center shrink-0" data-id="element-2549">
                            <img src={CARRIER_LOGOS[d.carrier]} alt={d.carrier} className="max-w-full max-h-full object-contain" data-id="element-2550" />
                          </div>
                          <span className="text-sm text-[#FAFAFA]" data-id="element-2551">
                            {d.carrier}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5" data-id="element-2552">
                        <div className="text-sm text-[#FAFAFA]" data-id="element-2553">
                          {d.recipient}
                        </div>
                        <div className="text-[11px] text-[#6B6B6B]" data-id="element-2554">
                          {d.city}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-xs font-mono text-[#9A9A9A]" data-id="element-2555">
                        {d.scheduledDate}
                      </td>
                      <td className="px-5 py-3.5 text-right" data-id="element-2556">
                        <span className={`font-mono font-semibold ${d.daysLate >= 3 ? 'text-[#EF4444]' : 'text-[#F5A524]'}`} data-id="element-2557">
                          +{d.daysLate} g
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-[#9A9A9A] max-w-[280px] truncate" data-id="element-2558">
                        {d.reason}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-2559">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border}`} data-id="element-2560">
                          <Icon className="h-3 w-3" data-id="element-2561" />
                          {d.status}
                        </span>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}